/**
 * The Inspector with nothing selected: the open level's overview (see
 * levelOverview.ts), and each position as a row that selects and frames its
 * zone, as double-clicking it in the Outliner does. Before a level is open,
 * only the hint shows.
 */
import { Fragment, useMemo } from "react";
import type { EntityId } from "../../world/entity";
import { useEditor } from "../EditorContext";
import { isPlaceholder } from "../format/placeholder";
import { useWorldSnapshot } from "../hooks/editorHooks";
import { levelOverview, projectLinks } from "./levelOverview";
import type { LevelOverview, OverviewLink, OverviewZone } from "./levelOverview";

const HINT = "Select an entity to see its properties.";

export function LevelOverviewPanel() {
  const { content } = useEditor();
  const world = useWorldSnapshot();
  const level = content.index.levels.find((entry) => entry.id === world.levelId);
  const overview = useMemo(() => level && levelOverview(world, level), [world, level]);
  const links = useMemo(
    () => (level ? projectLinks(content.seed, level.id) : []),
    [content.seed, level],
  );

  if (!overview) return <p className="kn-overview__hint">{HINT}</p>;
  return <Overview overview={overview} links={links} />;
}

function Overview({
  overview,
  links,
}: {
  overview: LevelOverview;
  links: readonly OverviewLink[];
}) {
  const { dispatch } = useEditor();
  const choose = (id: EntityId) => {
    void dispatch({ type: "selection.set", payload: { ids: [id] }, origin: "inspector" });
    void dispatch({ type: "camera.frame", payload: {}, origin: "inspector" });
  };

  return (
    <section className="kn-overview" aria-label="Level overview">
      <p className="kn-overview__eyebrow">{overview.kind} level</p>
      <h2 className="kn-overview__title">{overview.title}</h2>
      {overview.summary && (
        <p className="kn-overview__summary" data-placeholder={isPlaceholder(overview.summary)}>
          {overview.summary}
        </p>
      )}

      <dl className="kn-overview__facts">
        {overview.locations.length > 0 && (
          <>
            <dt>Location</dt>
            <dd>{overview.locations.join(" · ")}</dd>
          </>
        )}
        {links.map((link) => (
          <Fragment key={link.url}>
            <dt>{link.label}</dt>
            <dd>
              <a href={link.url} target="_blank" rel="noreferrer">
                {link.text}
              </a>
            </dd>
          </Fragment>
        ))}
        <dt>Asset</dt>
        <dd className="kn-overview__path">{overview.assetPath}</dd>
      </dl>

      {overview.zones.length > 0 && (
        <>
          <h3 className="kn-overview__heading">{overview.zonesLabel}</h3>
          <ul className="kn-overview__zones">
            {overview.zones.map((zone) => (
              <li key={zone.id}>
                <ZoneRow zone={zone} onChoose={choose} />
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="kn-overview__hint">{HINT}</p>
    </section>
  );
}

function ZoneRow({ zone, onChoose }: { zone: OverviewZone; onChoose: (id: EntityId) => void }) {
  return (
    <button
      type="button"
      className="kn-overview__zone"
      title={`Select and frame ${zone.name}`}
      onClick={() => {
        onChoose(zone.id);
      }}
    >
      <span className="kn-overview__zone-title" data-placeholder={isPlaceholder(zone.title)}>
        {zone.title}
      </span>
      {zone.detail && <span className="kn-overview__zone-detail">{zone.detail}</span>}
      <span className="kn-overview__zone-name">{zone.name}</span>
    </button>
  );
}
